import { SendLinkedinMessageQueueMessageType as MessageType } from "../generated/django-enums";
import { getGlobalHeadersAsObject } from "../global-headers";
import type { Group } from "../person/groups";
import type { DRFListResponseWrapper } from "../types";
import type { QueueTemplate } from "./email";

export { MessageType };

export type LinkedinMessageQueueSearchParams = {
	user__id?: number;
	person__id?: number;
	assignment__id?: number;
	from_template__name?: string;
	up_group?: number | "all";
	sent?: boolean;
	send_immediately?: boolean;
	manually?: boolean;
	acknowledged_error?: boolean;
	error?: string;
	error_isnull?: boolean;
	limit?: number;
	offset?: number;
	ordering?: string;
};

type AddLinkedinMessageArgs = {
	to: number;
	from: number;
	text: string;
	sendImmediately: boolean;
	fromTemplate?: number;
	messageType?: MessageType;
	groupId?: number;
};

export type LinkedinMessage = {
	id: number;
	person: number;
	user: number;
	text: string;
	manually: boolean;
	send_immediately: boolean;
	from_template?: number;
	error?: string;
	message_type?: MessageType;
	user_name?: string;
	user_first_name?: string;
	type?: string;
	group_id?: number;
	context_urn?: string;
	group?: Group;
	created?: string;
	updated?: string;
	assignment_id?: number;
	assignment_name?: string;
	template_name?: string;
	/** Who wrote the pitch: the AI prompt version, else the template. */
	pitch_source_name?: string | null;
	vote_id?: number;
	cc_id?: number;
};

export type QueueLinkedinMessageV2 = {
	id?: number;
	created?: string;
	updated?: string;
	text: string;
	sent?: boolean;
	user: number;
	person: number;
	send_immediately?: boolean;
	manually?: boolean;
	from_template?: number;
	acknowledged_error?: boolean;
	error?: string;
	context_urn?: string;
	message_type?: string;
	person_name?: string;
	person_url?: string;
	user_name?: string;
	user_first_name?: string;
	assignment_id?: number;
	assignment_name?: string;
	template_name?: string;
	/** Who wrote the pitch: the AI prompt version, else the template. */
	pitch_source_name?: string | null;
	vote_id?: number;
	cc_id?: number;
	group_id?: number;
};

export const addLinkedinMessageToQueue = ({
	text,
	from,
	to,
	sendImmediately = false,
	fromTemplate,
	messageType = MessageType.MESSAGE,
	groupId,
}: AddLinkedinMessageArgs): Promise<LinkedinMessage> => {
	return fetch(`/api/v2/queuelinkedinmessage/`, {
		method: "POST",
		body: JSON.stringify({
			text,
			person: to,
			user: from,
			send_immediately: sendImmediately,
			manually: true,
			from_template: fromTemplate,
			message_type: messageType,
			group_id: groupId,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP ${res.status}`);
		}
		return res.json();
	});
};

export const updateLinkedinMessageOnQueue = (
	messageId: number,
	text: string
): Promise<LinkedinMessage> => {
	return fetch(`/api/v2/queuelinkedinmessage/${messageId}/`, {
		method: "PATCH",
		body: JSON.stringify({ text }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};

export const deleteLinkedinMessageOnQueue = (messageId: number): Promise<any> => {
	return fetch(`/api/v2/queuelinkedinmessage/${messageId}/`, {
		method: "DELETE",
		headers: {
			...getGlobalHeadersAsObject(),
		},
	});
};

export const bulkDeleteLinkedinMessageQueue = (
	ids: number[]
): Promise<{ deleted: number }> => {
	return fetch("/api/v2/queuelinkedinmessage/bulk_delete/", {
		method: "POST",
		body: JSON.stringify({ ids }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};

type PersonLinkedinQueueResponse = LinkedinMessage & { sent: boolean };

export const getLinkedinMessageQueueForPerson = (
	personId: number,
	options?: { includeSent?: boolean }
): Promise<PersonLinkedinQueueResponse[]> => {
	if (!personId) {
		throw new Error(
			"Required argument 'personId' was not supplied to getLinkedinMessageQueueForPerson"
		);
	}

	let url = `/api/v2/queuelinkedinmessage/?person__id=${personId}`;
	if (!options?.includeSent) {
		url += "&sent=false";
	}
	return fetch(url)
		.then((res) => res.json())
		.then(
			(res: DRFListResponseWrapper<PersonLinkedinQueueResponse[]>) =>
				res.results ?? []
		);
};

/**
 * Generic function to query LinkedinMessageQueue with belonging search params
 * @param params
 * @returns A promise holding array of QueueLinkedinMessageV2 object
 */
export type QueueAssignment = {
	id: number;
	name: string;
	group_name: string | null;
	count: number;
};

export type QueueUser = {
	id: number;
	username: string;
	count: number;
};

export const getLinkedinMessageQueueDistinctUsers = (
	params?: Partial<LinkedinMessageQueueSearchParams>
): Promise<QueueUser[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/queuelinkedinmessage/distinct_users/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getLinkedinMessageQueueDistinctAssignments = (
	params?: Partial<LinkedinMessageQueueSearchParams>
): Promise<QueueAssignment[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(
		`/api/v2/queuelinkedinmessage/distinct_assignments/${qs ? `?${qs}` : ""}`,
		{ headers: getGlobalHeadersAsObject() }
	).then((res) => res.json());
};

export const getLinkedinMessageQueueDistinctTemplates = (
	params?: Partial<LinkedinMessageQueueSearchParams>
): Promise<QueueTemplate[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(
		`/api/v2/queuelinkedinmessage/distinct_templates/${qs ? `?${qs}` : ""}`,
		{ headers: getGlobalHeadersAsObject() }
	).then((res) => res.json());
};

export const getLinkedinMessageQueue = (
	params: LinkedinMessageQueueSearchParams
): Promise<DRFListResponseWrapper<QueueLinkedinMessageV2[]>> => {
	const queryParams = Object.keys(params).map((key) => {
		return [key, params[key]];
	});
	return fetch(
		`/api/v2/queuelinkedinmessage/?${new URLSearchParams(queryParams).toString()}`,
		{
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		}
	).then(async (res) => {
		if (res.ok) return res.json();
		throw new Error(await res.json());
	});
};
